/**
 * The one place that decides whether an answer can be opened in the context
 * graph, and what URL that is.
 *
 * It is a single function rather than a `visible` predicate beside a `href`
 * builder because the two can never disagree: a link that cannot be built (no
 * chat, a message that is not persisted yet) is exactly the case where the
 * control must not be offered.
 */

/** `history-4401` — the id `Chat.tsx` gives a persisted message. */
const PERSISTED_MESSAGE_ID = /^history-(\d+)$/;

export interface ContextGraphLinkArgs {
	/** `isCabinetUiEnabled()` — the flag behind the whole graph surface. */
	enabled: boolean;
	/** The chat's public id, from the route. */
	chatId: string | null;
	/** The UI message id (`history-<db id>` once persisted). */
	messageId: string;
	/** This answer's verified evidence events. */
	evidenceCount: number;
	/** An answer still being written has no graph to show yet. */
	isStreaming: boolean;
}

/**
 * `/context-graph?chat=…&message=…` for an answer that has one, else `null`.
 *
 * The message id in the URL is the DATABASE id the graph API takes
 * (`GET /api/v3/context-graph/chats/{chat_public_id}/messages/{message_id}`,
 * where `message_id` is an int), not the `history-`-prefixed id the chat uses
 * for its own bookkeeping.
 */
export function contextGraphMessageLink({
	enabled,
	chatId,
	messageId,
	evidenceCount,
	isStreaming,
}: ContextGraphLinkArgs): string | null {
	if (!enabled || isStreaming || evidenceCount <= 0 || !chatId) return null;
	const dbId = persistedMessageDbId(messageId);
	if (dbId === null) return null;
	return (
		`/context-graph?chat=${encodeURIComponent(chatId)}` +
		`&message=${encodeURIComponent(dbId)}`
	);
}

/**
 * The DATABASE id behind a chat message id, or `null` while it is unpersisted.
 *
 * The URL above is one way to reach an answer's graph; the Analyst rail mounts
 * `ContextGraphAnswerView` in place of the thread instead (DOP-492), and needs
 * the same id that URL carries. Sharing one parse is what stops an in-rail
 * view and a link to the page from disagreeing about which message is which.
 */
export function persistedMessageDbId(messageId: string): string | null {
	const persisted = PERSISTED_MESSAGE_ID.exec(messageId);
	return persisted === null ? null : persisted[1];
}

/**
 * The way BACK: `/chat/<chatId>?message=<db id>`.
 *
 * A message neighbourhood is not a destination the way the sidebar's Context
 * graph entry is — the reader was reading an answer and stepped sideways to see
 * what it was built from. Without this they landed on a top-level tab with the
 * conversation gone and no route back to the paragraph they came from.
 *
 * Lives beside `contextGraphMessageLink` on purpose: the two are one round
 * trip, and a return link that disagreed with the outbound one about which
 * message is which would be worse than none.
 */
export function chatReturnLink(
	chatId: string | null,
	messageId: string | null,
): string | null {
	if (!chatId) return null;
	const base = `/chat/${encodeURIComponent(chatId)}`;
	if (!messageId || !/^\d+$/.test(messageId)) return base;
	return `${base}?message=${encodeURIComponent(messageId)}`;
}

/** The chat's own id for a database message id — what `MessageList` keys on. */
export function chatMessageDomId(databaseId: string): string {
	return `history-${databaseId}`;
}
